import React, { useState } from 'react'
import { Search, History, ArrowLeft } from 'lucide-react'
import { ConversationItem } from './ConversationItem'
import { groupConversations, type Conversation } from '../utils/conversationUtils'

interface ConversationHistoryProps {
    conversations: Conversation[]
    currentConversationId: string | null
    onConversationClick: (conversation: Conversation) => void
    onConversationDelete: (id: string, e: React.MouseEvent) => void
    onClose: () => void
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({
    conversations,
    currentConversationId,
    onConversationClick,
    onConversationDelete,
    onClose
}) => {
    const [searchTerm, setSearchTerm] = useState('')

    // Filter conversations based on search
    const filteredConversations = conversations.filter(conv =>
        conv.title.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
        <div className="flex-1 flex flex-col bg-white dark:bg-black overflow-hidden touch-pan-y">
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-800">
                <div className="p-4 max-w-4xl mx-auto">
                    {/* Mobile Back Button + Title */}
                    <div className="flex items-center gap-3 mb-3">
                        <button
                            onClick={onClose}
                            className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors md:hidden"
                            aria-label="Voltar ao chat"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                        </button>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Histórico de Conversas
                        </h3>
                    </div>

                    {/* Search Input */}
                    <div className="relative group">
                        {/* Gradient border wrapper */}
                        <div className="rounded-full p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:dark:bg-gradient-to-br group-focus-within:dark:from-blue-400/40 group-focus-within:dark:via-purple-500/40 group-focus-within:dark:to-pink-400/30 transition-all duration-200">
                            <div className="relative bg-white dark:bg-gray-950 rounded-full">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 z-10" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar conversas..."
                                    className="w-full pl-9 pr-3 h-7 bg-transparent border-0 rounded-full dark:text-white dark:placeholder-gray-500 focus:ring-0 text-sm focus:outline-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-2 max-w-4xl mx-auto h-full">
                    {filteredConversations.length === 0 ? (
                        /* Empty State */
                        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                <History className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                            </div>
                            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
                                {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa salva'}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {searchTerm
                                    ? 'Tente usar outros termos de busca'
                                    : 'Suas conversas anteriores aparecerão aqui'}
                            </p>
                        </div>
                    ) : (
                        /* Grouped Conversations */
                        <div className="space-y-6">
                            {Object.entries(groupConversations(filteredConversations)).map(
                                ([groupName, groupConvs]) =>
                                    groupConvs.length > 0 && (
                                        <div key={groupName}>
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                                                {groupName}
                                            </h3>
                                            <div className="space-y-0.5">
                                                {groupConvs.map(conv => (
                                                    <ConversationItem
                                                        key={conv.id}
                                                        id={conv.id}
                                                        title={conv.title}
                                                        timestamp={conv.timestamp}
                                                        isActive={currentConversationId === conv.id}
                                                        onClick={() => onConversationClick(conv)}
                                                        onDelete={(e) => onConversationDelete(conv.id, e)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
